import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./dialog";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import { Textarea } from "./textarea";
import { RadioGroup, RadioGroupItem } from "./radio-group";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import SearchableDropdown from "@/components/AddPrescriptionItem/SearchableDropdown";

export interface EmergencySupplyMedicationItem {
  id: string;
  medication: string;
  supplied: "yes" | "no" | "";
  packBrand?: string;
  quantity?: string;
  daysSupply?: string;
  doseInstructions?: string;
  notes?: string;
  notSuppliedReason?: string;
  notSuppliedReasonOther?: string;
}

interface EmergencySupplyMedicationModalProps {
  trigger: React.ReactNode;
  onSave: (medication: EmergencySupplyMedicationItem) => void;
  editMode?: EmergencySupplyMedicationItem;
  isDarkMode?: boolean;
}

const COMMON_MEDICATIONS = [
  "Amlodipine 5mg tablets",
  "Amlodipine 10mg tablets",
  "Atorvastatin 20mg tablets",
  "Atorvastatin 40mg tablets",
  "Bisoprolol 5mg tablets",
  "Bisoprolol 10mg tablets",
  "Levothyroxine 25mcg tablets",
  "Levothyroxine 50mcg tablets",
  "Levothyroxine 100mcg tablets",
  "Metformin 500mg tablets",
  "Metformin 850mg tablets",
  "Ramipril 2.5mg capsules",
  "Ramipril 5mg capsules",
  "Ramipril 10mg capsules",
  "Omeprazole 20mg capsules",
  "Omeprazole 40mg capsules",
  "Salbutamol 100mcg inhaler",
  "Simvastatin 20mg tablets",
  "Simvastatin 40mg tablets",
  "Paracetamol 500mg tablets",
  "Ibuprofen 400mg tablets",
  "Aspirin 75mg tablets",
  "Clopidogrel 75mg tablets",
  "Warfarin 1mg tablets",
  "Warfarin 3mg tablets",
  "Warfarin 5mg tablets",
];

const NOT_SUPPLIED_REASONS = [
  {
    value: "schedule-controlled",
    label:
      "A: Item not able to be supplied under emergency supply regulations (e.g. Schedule 1, 2 or 3 controlled drug)",
  },
  {
    value: "eps-prescription",
    label: "B: EPS Prescription dispensed for patient",
  },
  {
    value: "not-necessary",
    label:
      "C: Pharmacist determined that supply not necessary (e.g. not clinically appropriate or concern about abuse of service)",
  },
  {
    value: "not-in-stock",
    label: "D: Item not in stock",
  },
  {
    value: "patient-bought",
    label: "F: Patient bought item",
  },
  {
    value: "other",
    label: "Other",
  },
];

export function EmergencySupplyMedicationModal({
  trigger,
  onSave,
  editMode,
  isDarkMode = false,
}: EmergencySupplyMedicationModalProps) {
  const [open, setOpen] = useState(false);
  const [medicationSearchOpen, setMedicationSearchOpen] = useState(false);

  const [medication, setMedication] = useState(editMode?.medication || "");
  const [supplied, setSupplied] = useState<"yes" | "no" | "">(
    editMode?.supplied || "",
  );
  const [packBrand, setPackBrand] = useState(editMode?.packBrand || "");
  const [quantity, setQuantity] = useState(editMode?.quantity || "");
  const [daysSupply, setDaysSupply] = useState(editMode?.daysSupply || "");
  const [doseInstructions, setDoseInstructions] = useState(
    editMode?.doseInstructions || "",
  );
  const [notes, setNotes] = useState(editMode?.notes || "");
  const [notSuppliedReason, setNotSuppliedReason] = useState(
    editMode?.notSuppliedReason || "",
  );
  const [notSuppliedReasonOther, setNotSuppliedReasonOther] = useState(
    editMode?.notSuppliedReasonOther || "",
  );

  const handleReset = () => {
    setMedication("");
    setSupplied("");
    setPackBrand("");
    setQuantity("");
    setDaysSupply("");
    setDoseInstructions("");
    setNotes("");
    setNotSuppliedReason("");
    setNotSuppliedReasonOther("");
  };

  useEffect(() => {
    if (open && editMode) {
      setMedication(editMode.medication);
      setSupplied(editMode.supplied);
      setPackBrand(editMode.packBrand || "");
      setQuantity(editMode.quantity || "");
      setDaysSupply(editMode.daysSupply || "");
      setDoseInstructions(editMode.doseInstructions || "");
      setNotes(editMode.notes || "");
      setNotSuppliedReason(editMode.notSuppliedReason || "");
      setNotSuppliedReasonOther(editMode.notSuppliedReasonOther || "");
    } else if (open && !editMode) {
      handleReset();
    }
  }, [open, editMode]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!medication) {
      return;
    }

    const medicationItem: EmergencySupplyMedicationItem = {
      id: editMode?.id || `med-${Date.now()}`,
      medication,
      supplied,
      ...(supplied === "yes" && {
        packBrand,
        quantity,
        daysSupply,
        doseInstructions,
        notes,
      }),
      ...(supplied === "no" && {
        notSuppliedReason,
        notSuppliedReasonOther,
      }),
    };

    onSave(medicationItem);
    handleReset();
    setOpen(false);
  };
  const handleUpdateMedication = (
    id: string,
    PrescriptionItemId: string,
    medicationName: string,
  ) => {
    setMedication(medicationName);
    // const medication = medications.find((med) => med.name === medicationName);
    // if (medication) {
    //   handleUpdatePrescriptionItem(id, {
    //     medication: medicationName,
    //     PrescriptionItemId: PrescriptionItemId,
    //     // contraindications: medication.contraindications,
    //     // warnings: medication.warnings,
    //   });
    // }
  };
  // const handleUpdatePrescriptionItem = (
  //   id: string,
  //   updates: Partial<(typeof prescriptionItems)[0]>,
  // ) => {
  //   const result =
  //     updates.dosage !== undefined ? { dosage: updates.dosage } : {};
  //   const hasDosage = Object.keys(result).length > 0;
  //   if (hasDosage) {
  //     let valueToSet;
  //     const endsWithSpace = updates.dosage.endsWith(" ");

  //     if (endsWithSpace) {
  //       const trimmedVal = updates.dosage.slice(0, -1);
  //       const words = trimmedVal.split(" ");
  //       const lastWord = words[words.length - 1];

  //       if (/^\d+$/.test(lastWord)) {
  //         words[words.length - 1] = numberToDoseText(parseInt(lastWord, 10));
  //         valueToSet = words.join(" ") + " ";
  //         updates.dosage = valueToSet;
  //       } else {
  //         const matched = doseShortCodeList?.find(
  //           (entry) =>
  //             entry?.shortCode?.toLowerCase() === lastWord.toLowerCase(),
  //         );

  //         if (matched) {
  //           words[words.length - 1] = matched.description;
  //           valueToSet = words.join(" ") + " ";
  //           updates.dosage = valueToSet;
  //         }
  //       }
  //     }
  //     setPrescriptionItems(
  //       prescriptionItems.map((item) =>
  //         item.id === id ? { ...item, ...updates } : item,
  //       ),
  //     );
  //   } else {
  //     setPrescriptionItems(
  //       prescriptionItems.map((item) =>
  //         item.id === id ? { ...item, ...updates } : item,
  //       ),
  //     );
  //   }

  //   setPrescriptionItems(
  //     prescriptionItems.map((item) =>
  //       item.id === id ? { ...item, ...updates } : item,
  //     ),
  //   );
  // };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className={`sm:max-w-[600px] max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`text-lg font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {editMode ? "Edit Medication Item" : "Add Medication Item"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Medication Search */}
          {/* <div className="space-y-2">
            <Label
              className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Medication *
            </Label>
            <Popover open={medicationSearchOpen} onOpenChange={setMedicationSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={medicationSearchOpen}
                  className={`w-full justify-between ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-gray-200"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  {medication || "Search for medication..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className={`w-[500px] p-0 ${isDarkMode ? "bg-gray-800 border-gray-600" : "bg-white"}`}>
                <Command className={isDarkMode ? "bg-gray-800" : "bg-white"}>
                  <CommandInput
                    placeholder="Search medications..."
                    value={medication}
                    onValueChange={setMedication}
                    className={isDarkMode ? "text-gray-200" : "text-gray-900"}
                  />
                  <CommandList>
                    <CommandEmpty className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
                      No medication found. Type to add custom medication.
                    </CommandEmpty>
                    <CommandGroup>
                      {COMMON_MEDICATIONS.filter((med) =>
                        med.toLowerCase().includes(medication.toLowerCase())
                      ).map((med) => (
                        <CommandItem
                          key={med}
                          value={med}
                          onSelect={(currentValue) => {
                            setMedication(currentValue === medication ? "" : currentValue);
                            setMedicationSearchOpen(false);
                          }}
                          className={isDarkMode ? "text-gray-200" : "text-gray-900"}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              medication === med ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {med}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div> */}
          <div className="pc-modal-input-field med-field">
            <label>Medication</label>
            <SearchableDropdown
              className={`searchable-dropdown ${""}`}
              searchType="drug"
              label="name"
              id="id"
              selectedVal={medication}
              handleChange={(val) => {
                handleUpdateMedication(
                  "item.id",
                  val.prescribedItemId,
                  val.drugName,
                );
                // handleChange(item.id, "value", val, item);
              }}
            />
            {/* {errors.value && (
                                  <div className="error-message">{errors.value}</div>
                                )} */}
          </div>

          {/* Supplied Question */}
          <div className="space-y-3">
            <Label
              className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Supplied to the patient as an emergency supply? *
            </Label>
            <RadioGroup
              value={supplied}
              onValueChange={(value: "yes" | "no") => setSupplied(value)}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="supplied-yes" />
                <Label
                  htmlFor="supplied-yes"
                  className={`text-sm cursor-pointer ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Yes
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="supplied-no" />
                <Label
                  htmlFor="supplied-no"
                  className={`text-sm cursor-pointer ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  No
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* If YES - Supply Details */}
          {supplied === "yes" && (
            <div className="space-y-4 p-4 border rounded-lg bg-blue-50/50 dark:bg-blue-900/10">
              <div className="space-y-2">
                <Label
                  htmlFor="packBrand"
                  className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Pack / Brand Supplied *
                </Label>
                <Input
                  id="packBrand"
                  value={packBrand}
                  onChange={(e) => setPackBrand(e.target.value)}
                  placeholder="e.g., Pfizer 28 pack"
                  className={
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-gray-200"
                      : ""
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="quantity"
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Quantity *
                  </Label>
                  <Input
                    id="quantity"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="e.g., 28"
                    className={
                      isDarkMode
                        ? "bg-gray-800 border-gray-600 text-gray-200"
                        : ""
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="daysSupply"
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Days Supply *
                  </Label>
                  <Input
                    id="daysSupply"
                    value={daysSupply}
                    onChange={(e) => setDaysSupply(e.target.value)}
                    placeholder="e.g., 28"
                    className={
                      isDarkMode
                        ? "bg-gray-800 border-gray-600 text-gray-200"
                        : ""
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="doseInstructions"
                  className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Dose Instructions *
                </Label>
                <Textarea
                  id="doseInstructions"
                  value={doseInstructions}
                  onChange={(e) => setDoseInstructions(e.target.value)}
                  placeholder="e.g., Take one tablet daily with food"
                  rows={2}
                  className={
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-gray-200"
                      : ""
                  }
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="notes"
                  className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Additional Notes
                </Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional information..."
                  rows={2}
                  className={
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-gray-200"
                      : ""
                  }
                />
              </div>
            </div>
          )}

          {/* If NO - Reason for not supplying */}
          {supplied === "no" && (
            <div className="space-y-3 p-4 border rounded-lg bg-amber-50/50 dark:bg-amber-900/10">
              <Label
                className={`text-sm font-medium transition-colors duration-300 ${
                  isDarkMode ? "text-gray-200" : "text-gray-700"
                }`}
              >
                Reason for not supplying *
              </Label>
              <RadioGroup
                value={notSuppliedReason}
                onValueChange={setNotSuppliedReason}
              >
                {NOT_SUPPLIED_REASONS.map((reason) => (
                  <div
                    key={reason.value}
                    className="flex items-start space-x-3 py-1"
                  >
                    <RadioGroupItem
                      value={reason.value}
                      id={`reason-${reason.value}`}
                      className="mt-1 flex-shrink-0"
                    />
                    <Label
                      htmlFor={`reason-${reason.value}`}
                      className={`text-sm cursor-pointer flex-1 leading-relaxed ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {reason.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              {notSuppliedReason === "other" && (
                <div className="space-y-2 mt-3">
                  <Label
                    htmlFor="notSuppliedReasonOther"
                    className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    Please specify
                  </Label>
                  <Textarea
                    id="notSuppliedReasonOther"
                    value={notSuppliedReasonOther}
                    onChange={(e) => setNotSuppliedReasonOther(e.target.value)}
                    placeholder="Please specify the reason..."
                    rows={2}
                    className={
                      isDarkMode
                        ? "bg-gray-800 border-gray-600 text-gray-200"
                        : ""
                    }
                  />
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                handleReset();
                setOpen(false);
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(e as any);
              }}
              className={`flex-1 ${
                isDarkMode
                  ? "bg-white text-black hover:bg-gray-200"
                  : "bg-slate-900 hover:bg-slate-800"
              }`}
              disabled={
                !medication ||
                !supplied ||
                (supplied === "yes" &&
                  (!packBrand ||
                    !quantity ||
                    !daysSupply ||
                    !doseInstructions)) ||
                (supplied === "no" && !notSuppliedReason) ||
                (notSuppliedReason === "other" && !notSuppliedReasonOther)
              }
            >
              {editMode ? "Update Medication" : "Add Medication"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
